import { AxiosError } from "axios";

import type { ErrorEnvelope } from "../api/envelope";

/**
 * The raw axios message for a non-2xx response, e.g.
 * "Request failed with status code 422". We never want to show this to a user
 * — the backend always ships a human-readable `error.message` in the envelope
 * ({ success:false, error:{ code, message, requestId } }). This matcher lets us
 * detect + discard the axios placeholder when falling back.
 */
const RAW_AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+$/i;

/** Pull the backend envelope's `error` object off any caught value, if present. */
function readErrorEnvelope(
  err: unknown,
): ErrorEnvelope["error"] | undefined {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | ErrorEnvelope
      | { error?: ErrorEnvelope["error"] }
      | undefined;
    return data?.error;
  }
  return undefined;
}

type ValidationIssue = { path?: string; message?: string };

function firstValidationIssue(error: ErrorEnvelope["error"]): string | undefined {
  const details = error.details;
  if (!details || typeof details !== "object") return undefined;
  const issues = (details as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) return undefined;

  const issue = issues.find(
    (candidate): candidate is ValidationIssue =>
      !!candidate && typeof candidate === "object",
  );
  const message = issue?.message?.trim();
  if (!message) return undefined;
  const path = issue?.path?.trim();
  return path ? `${path}: ${message}` : message;
}

/** The backend error `code` (e.g. "work-for-use-user-id"), if any. */
export function getApiErrorCode(err: unknown): string | undefined {
  return readErrorEnvelope(err)?.code;
}

/**
 * Resolve the best user-facing message for any caught value.
 *
 * Priority:
 *   1. Backend envelope `error.message` (the friendly, server-authored text).
 *   2. A network/timeout hint for transport failures (no response body).
 *   3. A non-axios `Error.message` (e.g. Cognito/Amplify errors) — but never
 *      the raw "Request failed with status code NNN" placeholder.
 *   4. The caller's `fallback`.
 *
 * Use this everywhere an API error is shown to the user so we surface the
 * server message instead of "Request failed with status code 422".
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const envelope = readErrorEnvelope(err);
  const envelopeMessage = envelope?.message?.trim();
  if (envelopeMessage) {
    // The common Nest validation title is intentionally generic. Preserve it,
    // but include the first normalized Zod issue so users and support can see
    // which snapshot field actually failed.
    const issue =
      envelopeMessage.toLowerCase() === "validation failed" && envelope
        ? firstValidationIssue(envelope)
        : undefined;
    return issue ? `${envelopeMessage} — ${issue}` : envelopeMessage;
  }

  if (err instanceof AxiosError) {
    if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
      return "Cannot reach the server. Please check your connection and try again.";
    }
    // No structured body (e.g. gateway/5xx with empty payload) → fallback,
    // never the raw axios status placeholder.
    return fallback;
  }

  if (err instanceof Error) {
    const message = err.message?.trim();
    if (message && !RAW_AXIOS_STATUS_MESSAGE.test(message)) {
      return message;
    }
  }

  return fallback;
}
